import { motion } from 'framer-motion';

/**
 * Reusable "story" section with theme variants. Reduces page monotony.
 * theme: 'ink' (dark, default) | 'lime' | 'sage' | 'cream' | 'butter'
 */
const THEMES = {
  ink: {
    section: 'bg-ev-ink',
    label: 'text-ev-lime',
    heading: 'text-white',
    sub: 'text-white/70',
    card: 'bg-white/[0.04] border border-white/10',
    kicker: 'text-ev-lime',
    title: 'text-white',
    scene: 'text-white/70',
  },
  lime: {
    section: 'bg-ev-lime',
    label: 'text-ev-ink/70',
    heading: 'text-ev-ink',
    sub: 'text-ev-ink/75',
    card: 'bg-ev-ink text-white border border-ev-ink',
    kicker: 'text-ev-lime',
    title: 'text-white',
    scene: 'text-white/75',
  },
  sage: {
    section: 'bg-ev-sage',
    label: 'text-ev-ink/70',
    heading: 'text-ev-ink',
    sub: 'text-ev-ink/75',
    card: 'bg-ev-cream border border-ev-ink/5',
    kicker: 'text-ev-ink/60',
    title: 'text-ev-ink',
    scene: 'text-ev-ink/70',
  },
  cream: {
    section: 'bg-ev-cream',
    label: 'text-ev-ink/60',
    heading: 'text-ev-ink',
    sub: 'text-ev-ink/70',
    card: 'bg-white border border-ev-gray-border',
    kicker: 'text-ev-ink/60',
    title: 'text-ev-ink',
    scene: 'text-ev-gray',
  },
  butter: {
    section: 'bg-ev-butter',
    label: 'text-ev-ink/70',
    heading: 'text-ev-ink',
    sub: 'text-ev-ink/75',
    card: 'bg-ev-cream border border-ev-ink/5',
    kicker: 'text-ev-ink/60',
    title: 'text-ev-ink',
    scene: 'text-ev-ink/70',
  },
};

export default function SectionTwoCards({ id, testId, label, h, sub, items, theme = 'ink' }) {
  const t = THEMES[theme] || THEMES.ink;
  const cols = items.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
  return (
    <section id={id} className={`${t.section} py-24 md:py-32 px-6 md:px-10`} data-testid={testId}>
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-[820px] mx-auto text-center mb-16">
          <p className={`font-body text-[10px] uppercase tracking-[0.4em] ${t.label} mb-6`}>{label}</p>
          <h2
            className={`font-display font-light ${t.heading} lowercase mb-6`}
            style={{ fontSize: 'clamp(2rem, 5vw, 4rem)', letterSpacing: '-0.03em' }}>
            {h}
          </h2>
          {sub && <p className={`font-body text-base md:text-lg ${t.sub} leading-relaxed`}>{sub}</p>}
        </motion.div>

        <div className={`grid grid-cols-1 ${cols} gap-5 md:gap-6 max-w-[1200px] mx-auto`}>
          {items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`${t.card} rounded-[1.5rem] p-8 md:p-10 flex flex-col`}
              data-testid={`${testId}-item-${i}`}>
              {item.kicker && (
                <p className={`font-body text-[10px] uppercase tracking-[0.3em] ${t.kicker} mb-4`}>{item.kicker}</p>
              )}
              <h3 className={`font-display text-2xl md:text-3xl ${t.title} lowercase mb-4 leading-snug`}>{item.title}</h3>
              <p className={`font-body text-sm md:text-base ${t.scene} leading-relaxed`}>{item.scene}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
